import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Autopay, BillerGroup } from "model";

/**
 * @class Biller
 * @classdesc Class definition for the record type from model
 * @param {OBJECT} record - The record from the model to init class
 * @extends BCMSRecord
 * @example const record = new Biller(model.billers[0]);
 * 
 * @property {STRING} _name - The name of the biller
 * @property {STRING} _nickname - The nickname of the biller
 * @property {STRING} _number - The number of the biller
 * @property {STRING} _address - The address of the biller
 * @property {STRING} _city - The city of the biller
 * @property {STRING} _state - The state of the biller
 * @property {STRING} _zip - The zip of the biller
 * @property {STRING} _phone - The phone of the biller
 * @property {STRING} _paymentType - The payment type of the biller
 * @property {STRING} _id - The id of the biller
 * @property {STRING} _type - The type of the biller
 * @property {STRING} _eBillStatus - The e bill status of the biller
 * @property {STRING} _addType - The add type of the biller
 * @property {STRING} _logo - The logo of the biller
 * @property {STRING} _confirmInfo - The confirm info of the biller
 * @property {Autopay} _autopay - The autopay associated to the biller
 * @property {STRING} _eBillTrialPeriod - The value that determines if the biller is in a trial period
 * @property {BillerGroup} _billerGroup - The biller group that this biller is in.
 */
export default class Biller extends BCMSRecord{   
    _name; 
    _nickname; 
    _number;
    _address;
    _city;
    _state; 
    _zip;
    _phone; 
    _paymentType; 
    _id; 
    _type; 
    _eBillStatus; 
    _addType; 
    _logo; 
    _confirmInfo;
    _eBillTrialPeriod;
    _autopay;
    _billerGroup;

    constructor (record){
       super(record)
    }
  
    /**
     * @function isActiveEBiller
     * @instance
     * @returns {BOOLEAN}
     * @description Returns a boolean if the e bill status is active
     * @memberof Biller
     */
    get isActiveEBiller() {
        return this.eBillStatus.toLowerCase() === "active";
    }

    get hasEbillAutopay() {
        return this.isActiveEBiller && Autopay.getByBillerID(this.id).length >= 1;
    }

    // Biller.firstBiller :: OBJECT -> biller
    // Returns an instance of Biller for the first biller of the model 

    /**
     * @function firstBiller
     * @static
     * @property {OBJECT}
     * @returns {Biller}
     * @description Return the first biller from the model
     * @memberof Biller
     */
    static firstBiller(model){
        return  Biller.init(model.billers[0],model);
    }

    /**
     * @function getFirstEBiller
     * @static
     * @returns {Biller}
     * @description Gets the first biller that has an ebill status of active
     * @memberof Biller
     */
    static getFirstEBiller() {
        return Biller.initAll().find(biller => biller.eBillStatus === "Active");
    }

    /**
     * @function getVirtualCardDisclaimer
     * @static
     * @returns {String}
     * @description Returns a virtual card disclaimer used in various pages.
     * @memberof Biller
     */
    static getVirtualCardDisclaimer(singular) {
        return singular
            ? `This payment may be delivered as a secure, single use virtual card to the biller.
                If the biller does not accept payment, your payment will be reissued as a check to the biller.`
            : `These payments may be delivered as a secure, single use virtual card to the biller. 
                If the biller does not accept payment, your payment will be reissued as a check to the biller.`;
    }
}

Biller.define({
    "recordType":"billers",
    "recordIDProp":"billerID",
    "className" : "Biller",
    "recordProperties":[
        /**
         * @name name
         * @instance
         * @property get {STRING} - Gets the name of the biller
         * @property set {STRING} - Sets the name of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("name","_name","billerName"),

        /**
         * @name nickname
         * @instance
         * @property get {STRING} - Gets the nickname of the biller
         * @property set {STRING} - Sets the nickname of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("nickname","_nickname","billerNickname"),

        /**
         * @name number
         * @instance
         * @property get {STRING} - Gets the number of the biller
         * @property set {STRING} - Sets the number of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("number","_number","billerNumber"),

        /**
         * @name city
         * @instance
         * @property get {STRING} - Gets the city of the biller
         * @property set {STRING} - Sets the city of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("city","_city","billerCity"),

        /**
         * @name address
         * @instance
         * @property get {STRING} - Gets the address of the biller
         * @property set {STRING} - Sets the address of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("address","_address","billerAddress"),

        /**
         * @name state
         * @instance
         * @property get {STRING} - Gets the state of the biller
         * @property set {STRING} - Sets the state of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("state","_state","billerState"),

        /**
         * @name zip
         * @instance
         * @property get {STRING} - Gets the zip of the biller
         * @property set {STRING} - Sets the zip of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("zip","_zip","billerZip"),

        /**
         * @name phone
         * @instance
         * @property get {STRING} - Gets the phone of the biller
         * @property set {STRING} - Sets the phone of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("phone","_phone","billerPhone"),

        /**
         * @name paymentType
         * @instance
         * @property get {STRING} - Gets the paymentType of the biller
         * @property set {STRING} - Sets the paymentType of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("paymentType","_paymentType","billerPaymentType"),

        /**
         * @name id
         * @instance
         * @property get {STRING} - Gets the id of the biller
         * @property set {STRING} - Sets the id of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("id","_id","billerID"),

        /**
         * @name type
         * @instance
         * @property get {STRING} - Gets the type of the biller
         * @property set {STRING} - Sets the type of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("type","_type","billerType"),

        /**
         * @name eBillStatus
         * @instance
         * @property get {STRING} - Gets the eBillStatus of the biller
         * @property set {STRING} - Sets the eBillStatus of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("eBillStatus","_eBillStatus","billerEBillStatus"),

        /**
         * @name addType
         * @instance
         * @property get {STRING} - Gets the addType of the biller
         * @property set {STRING} - Sets the addType of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("addType","_addType","billerAddType"),

        /**
         * @name logo
         * @instance
         * @property get {STRING} - Gets the logo of the biller
         * @property set {STRING} - Sets the logo of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("logo","_logo","billerLogo"),

        /**
         * @name confirmInfo
         * @instance
         * @property get {STRING} - Gets the confirmInfo of the biller
         * @property set {STRING} - Sets the confirmInfo of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("confirmInfo","_confirmInfo","billerConfirmInfo"),
    
        /**
         * @name autopay
         * @instance
         * @property get {Autopay} - Gets the autopay of the biller
         * @property set {Autopay|STRING} - Sets the autopay of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToClass("autopay", "_autopay", () => Autopay, "billerAutopayID"),

        /**
         * @name eBillTrialPeriod
         * @instance
         * @property get {STRING} - Gets the eBillTrialPeriod of the biller
         * @property set {STRING} - Sets the eBillTirlaPeriod of the biller
         * @memberof Biller
         */
        BCMSRecordProperty.mapToString("eBillTrialPeriod", "_eBillTrialPeriod", "billerEBillTrialPeriod"),

        /**
         * @name billerGroup
         * @instance
         * @property get {BillerGroup} - Gets the billerGroup of the biller.
         * @property set {BillerGroup} - Sets the billerGroup of the biller.
         * @memberof Biller
         */
        BCMSRecordProperty.mapToClass("billerGroup", "_billerGroup", () => BillerGroup)
    ]
})


